import { useState } from "react";
import { Loader2 } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { usePoll } from "@/hooks/usePoll";
import { errMsg, get } from "@/lib/api";
import { fmt, fmtQty } from "@/lib/format";

const IVS = ["1m", "5m", "15m", "1h", "1d", "1w"];

const fmtTs = (iso, iv) => {
  const d = new Date(iso);
  const opt = ["1d", "1w"].includes(iv) ? { dateStyle: "medium" } : { dateStyle: "short", timeStyle: "short" };
  return d.toLocaleString("en-IN", { ...opt, hour12: false, timeZone: "Asia/Kolkata" });
};

export const CandlesPanel = ({ sid }) => {
  const [iv, setIv] = useState("15m");
  const { data, error } = usePoll(() => get(`/symbols/${sid}/candles`, { interval: iv }), 0, [sid, iv]);
  const rows = data ? [...data.candles].reverse().slice(0, 120) : null;
  return (
    <div data-testid="candles-panel" className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ToggleGroup type="single" value={iv} onValueChange={(v) => v && setIv(v)} className="gap-1">
          {IVS.map((x) => (
            <ToggleGroupItem key={x} value={x} data-testid={`candle-interval-${x}`}
              className="h-7 rounded border border-slate-800 px-2.5 font-mono text-[11px] data-[state=on]:border-cyan-600 data-[state=on]:bg-cyan-950/50 data-[state=on]:text-cyan-300">
              {x}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {data && <span data-testid="candles-count" className="font-mono text-[10px] text-slate-500">{data.count} candles · {data.kite_interval} · {data.source}</span>}
      </div>
      {error && <div data-testid="candles-error" className="rounded border border-rose-900/60 bg-rose-950/30 p-3 text-xs text-rose-300">{errMsg(error)}</div>}
      {!rows && !error && <div className="flex h-32 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-500" /></div>}
      {rows && (
        <div className="scroll-thin max-h-[340px] overflow-auto rounded border border-slate-800">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-900">
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                {["Time", "Open", "High", "Low", "Close", "Volume"].map((h, i) => (
                  <th key={h} className={`px-2 py-1.5 ${i ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono">
              {rows.map((c) => (
                <tr key={c.ts} className="border-t border-slate-800/60">
                  <td className="px-2 py-1 text-slate-400">{fmtTs(c.ts, iv)}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{fmt(c.open)}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{fmt(c.high)}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{fmt(c.low)}</td>
                  <td className={`px-2 py-1 text-right font-semibold ${c.close >= c.open ? "text-emerald-400" : "text-rose-400"}`}>{fmt(c.close)}</td>
                  <td className="px-2 py-1 text-right text-slate-500">{fmtQty(c.volume)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="py-6 text-center text-slate-500">No candles in range</div>}
        </div>
      )}
    </div>
  );
};
